/**
 * Where the save file actually lives.
 *
 * Split from src/systems/save.ts so the schema and its migrations stay testable
 * in Node with no DOM. This half is the part that has to survive a hostile
 * browser: private mode, a full quota, a disabled-storage policy, a corrupted
 * value written by a crashed tab. None of those may stop the game booting, so
 * every operation here fails soft and the game carries on with an in-memory save.
 *
 * A child losing their stars is bad. A child unable to start the game because
 * their stars could not be read is much worse.
 */

import { migrate, type SaveData } from '@/systems/save';
import { warnOnce } from '@/core/assert';

export const SAVE_KEY = 'cat-game.save.v1';

export interface SaveStore {
  load(): SaveData;
  save(data: SaveData): void;
  clear(): void;
  /** False once a write has failed - the HUD may want to say "not saving". */
  readonly writable: boolean;
}

/** Always works, never persists. Used in tests and as the fallback. */
export class MemorySaveStore implements SaveStore {
  private data: SaveData | null = null;
  readonly writable = true;

  load(): SaveData {
    return this.data ? structuredClone(this.data) : migrate(null);
  }

  save(data: SaveData): void {
    this.data = structuredClone(data);
  }

  clear(): void {
    this.data = null;
  }
}

export class LocalSaveStore implements SaveStore {
  private failed = false;

  constructor(private readonly key = SAVE_KEY) {}

  get writable(): boolean {
    return !this.failed;
  }

  load(): SaveData {
    try {
      const raw = localStorage.getItem(this.key);
      // migrate() handles null, garbage and every past schema. It never throws,
      // so a parse failure is the only thing that can land in the catch.
      return migrate(raw === null ? null : JSON.parse(raw));
    } catch (error) {
      warnOnce('save:load', `Could not read the save file, starting fresh: ${String(error)}`);
      return migrate(null);
    }
  }

  save(data: SaveData): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(data));
      this.failed = false;
    } catch (error) {
      // Quota exceeded, private mode, or storage disabled by policy.
      this.failed = true;
      warnOnce('save:write', `Could not write the save file: ${String(error)}`);
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(this.key);
    } catch {
      // Nothing useful to do; clear() is a convenience, not a guarantee.
    }
  }
}

/** A LocalSaveStore where storage exists, a MemorySaveStore where it does not. */
export function createSaveStore(key = SAVE_KEY): SaveStore {
  try {
    const probe = `${key}.probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return new LocalSaveStore(key);
  } catch {
    warnOnce('save:unavailable', 'localStorage is unavailable; progress will not be kept.');
    return new MemorySaveStore();
  }
}
